import keyBy from 'lodash/keyBy';
import uniqBy from 'lodash/uniqBy';
import { useCallback, useMemo } from 'react';

import { LocaleCategory, approvalStatusQualityMapper } from 'modules/campaigns/lib/constants';
import { Locale, WhatsAppActiveLocale } from 'modules/domains/template';
import {
  MetaApprovalStatusDefinition,
  MetaQualityRating,
} from 'modules/graphql/types/GraphQLTemplate';
import { Nullable } from 'utils/tsHelpers';

import { useTranslations } from '../../../../hooks/useTranslations';
import { LocaleOption } from '../../../types';
import { AddLocale } from './AddLocale';
import { LocaleSwitcherLabel } from './LocaleSwitcherLabel';

/* This is used to make sure we don't have falsy keys in maps */
const DEFAULT_LOCALE_KEY = '_DEFAULT_';

type UseLocaleOptionsProps = {
  allContentErrors: string[];
  allProjectLocales: Locale[];
  activeTemplateLocales: Locale[];
  activeWhatsAppTemplateLocales?: WhatsAppActiveLocale[];
  invalidWhatsAppLocales?: Locale[];
  validWhatsAppLocales?: Locale[];
  templateDefaultLocaleTag: string;
  isReadOnly: boolean;
  isWhatsAppTemplate?: boolean;
  isAssociatedWithActiveExperiment: boolean;
  hideErrorCount: boolean;
};

export const useLocaleOptions = ({
  isReadOnly,
  hideErrorCount,
  allContentErrors: localeTagsWithError,
  isAssociatedWithActiveExperiment,
  templateDefaultLocaleTag,
  allProjectLocales,
  activeTemplateLocales,
  activeWhatsAppTemplateLocales = [],
  invalidWhatsAppLocales = [],
  validWhatsAppLocales = [],
}: UseLocaleOptionsProps) => {
  const { t, ready } = useTranslations();
  const defaultLocaleSuffix = ready ? t('label.default') : 'Default';
  const addLocaleText = ready ? t('badge.add') : 'Add';

  const activeLocales = useMemo(
    () =>
      activeWhatsAppTemplateLocales?.length
        ? activeWhatsAppTemplateLocales
        : activeTemplateLocales.map((locale) => ({
            locale,
            metaApprovalStatus: undefined,
            qualityRating: undefined,
          })) || [],
    [activeWhatsAppTemplateLocales, activeTemplateLocales],
  );

  const activeLocalesMap = useMemo(
    () =>
      keyBy(
        activeLocales,
        (activeLocale) => activeLocale?.locale?.tag?.toLowerCase() || DEFAULT_LOCALE_KEY,
      ),
    [activeLocales],
  );

  /**
   * This makes sure we don't have locales as invalid and active or "ghost" (i.e exist on the BE but not displayed on the UI) active locales.
   */
  const invalidLocales = useMemo(
    () => invalidWhatsAppLocales.filter((locale) => !activeLocalesMap[locale?.tag?.toLowerCase()]),
    [invalidWhatsAppLocales, activeLocalesMap],
  );

  const invalidLocalesMap = useMemo(
    () => keyBy(invalidLocales, (locale) => locale?.tag?.toLowerCase() || DEFAULT_LOCALE_KEY),
    [invalidLocales],
  );

  const availableLocales = useMemo(
    () =>
      validWhatsAppLocales?.length
        ? validWhatsAppLocales
        : allProjectLocales.filter((locale) => {
            const tag = locale?.tag?.toLowerCase();
            return !activeLocalesMap[tag] && !invalidLocalesMap[tag];
          }),
    [validWhatsAppLocales, allProjectLocales, activeLocalesMap, invalidLocalesMap],
  );

  const errorsMap = useMemo(
    () => keyBy(localeTagsWithError, (tag) => tag || DEFAULT_LOCALE_KEY),
    [localeTagsWithError],
  );

  const toLocaleOption = useCallback(
    (
      locale: Locale,
      localeCategory: LocaleCategory,
      metaApprovalStatus?: Nullable<MetaApprovalStatusDefinition>,
      qualityRating?: MetaQualityRating,
    ): LocaleOption => {
      const { tag } = locale || {};

      const isActiveLocale = localeCategory === LocaleCategory.Active;
      const isInvalidLocale = localeCategory === LocaleCategory.NotAvailableForWhatsApp;
      const isAvailableLocale = localeCategory === LocaleCategory.Available;
      const isDefaultLocale = tag === templateDefaultLocaleTag;

      const isExperimentTemplateWithUnattachedLocale =
        isAssociatedWithActiveExperiment && !isActiveLocale;

      const isDisabled =
        isExperimentTemplateWithUnattachedLocale || (isInvalidLocale && !isActiveLocale);

      const localeHasError = !hideErrorCount && !!errorsMap[tag];

      const suffix = isDefaultLocale
        ? defaultLocaleSuffix
        : isAvailableLocale && <AddLocale text={addLocaleText} />;

      const approvalStatus = metaApprovalStatus
        ? approvalStatusQualityMapper(metaApprovalStatus, qualityRating)
        : null;

      const data = { ...locale, hasError: localeHasError };

      return {
        id: tag,
        label: (
          <LocaleSwitcherLabel
            isDisabled={isDisabled}
            localeHasError={localeHasError}
            projectLocale={locale}
            isAvailableLocale={isAvailableLocale}
            defaultLocaleSubLabel={
              isActiveLocale && approvalStatus && suffix ? defaultLocaleSuffix : ''
            }
          />
        ),
        value: tag,
        Suffix: approvalStatus || suffix,
        data,
        isDisabled,
        categoryId: localeCategory,
        tooltipContent: isExperimentTemplateWithUnattachedLocale
          ? t('tooltip.experimentLocale')
          : null,
      };
    },
    [
      t,
      errorsMap,
      hideErrorCount,
      defaultLocaleSuffix,
      templateDefaultLocaleTag,
      isAssociatedWithActiveExperiment,
    ],
  );

  const options: LocaleOption[] = useMemo(() => {
    const activeLocalesOptions = activeLocales.map((activeLocale) =>
      toLocaleOption(
        activeLocale.locale,
        LocaleCategory.Active,
        activeLocale.metaApprovalStatus,
        activeLocale.qualityRating,
      ),
    );
    const availableLocalesOptions = availableLocales.map((locale) =>
      toLocaleOption(locale, LocaleCategory.Available),
    );
    const invalidLocalesOptions = invalidLocales.map((locale) =>
      toLocaleOption(locale, LocaleCategory.NotAvailableForWhatsApp),
    );

    const localeOptions: LocaleOption[] = uniqBy(
      [...activeLocalesOptions, ...availableLocalesOptions, ...invalidLocalesOptions],
      'value',
    ); // Somehow we have duplicate locales in the list, this is a temporary fix to remove them.

    return isReadOnly
      ? localeOptions.filter((localeOption) => localeOption.categoryId === LocaleCategory.Active)
      : localeOptions;
  }, [isReadOnly, activeLocales, availableLocales, invalidLocales, toLocaleOption]);

  return options;
};
